import styled from "@emotion/styled";
import CircleIcon from "@mui/icons-material/Circle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { IOTPToolTip } from "components/common/Tooltip";
import SensorIcon from "components/gateways/SensorIcon";
import { useEffect, useRef, useState } from "react";

import { getOneRegistry } from "service/gatewayService";
import {
    buildSparklineSeriesWithMissingGaps,
    CardSensorReading,
    getCardMetric,
    getCardSensorReadings,
} from "service/proqueryService";
import {
    DeviceConfig,
    DeviceInfo,
    DeviceStatus,
    DeviceStatusStatus,
} from "types";
import { LDSU_STATUS } from "types/Ldsu";
import {
    IconCircle,
    ReadingTimeText,
    SensorContent,
    SensorLabel,
    SensorType,
} from ".";
import { SparklineChart } from "./components/SparklineChart";
import { CardMetric } from "./MultiSensor";
import { checkIfReachable, isBinarySensor } from "utils/functions";
import {
    getCurrentValueToDisplay,
    getLDSUStatusColor,
    getReadingDateTimeToDisplay,
} from "utils/gatewayFunctions";
import { DEFAULT_REPORT_RATE } from "constant/gateway";
import { UnknownDevice } from "constant";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";

const CardContent = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

const SensorInfo = styled.div`
    display: flex;
    flex-direction: column;
    width: calc(100% - 35px);
`;

const Value = styled.div<{ fontSize?: number | string }>`
    font-weight: 600;
    font-style: SemiBold;
    font-size: ${({ fontSize }) => fontSize ?? "18"}px;
    line-height: 100%;
    letter-spacing: 0%;
    text-align: right;
    color: #00cbaf;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

// Props for the card
interface SensorCardProps {
    devices: DeviceInfo[];
    panel: any;
    widgetId: string;
}

const fetchLDSUInfo = async (gatewayId: string, deviceId: string) => {
    const registryInfoRes = await getOneRegistry(gatewayId);

    const ldsuInfo = registryInfoRes.data?.configs?.find(
        (device: DeviceConfig) => device.UID === deviceId
    );

    if (ldsuInfo) {
        const ldsuStatus = registryInfoRes.data?.device_statuses?.find(
            (device: DeviceStatus) => device.UID === deviceId
        )?.status;

        return { ...ldsuInfo, status: ldsuStatus };
    }

    return ldsuInfo;
};

const getLDSUStatusLabel = (
    readings: CardSensorReading[],
    deviceConfig: DeviceConfig & {
        status?: DeviceStatusStatus;
    }
) => {
    const lastActiveTimestamps = (readings ?? []).map(
        (reading) => reading.lastActive ?? 0
    );
    const latestTs = Math.max(...lastActiveTimestamps);

    const isReachable = checkIfReachable(
        latestTs,
        deviceConfig?.INTVL ?? DEFAULT_REPORT_RATE
    );
    let statusLabel;

    if (deviceConfig?.status === DeviceStatusStatus.NOT_READY) {
        statusLabel = LDSU_STATUS.NOT_READY;
    } else if (isReachable) {
        statusLabel = LDSU_STATUS.REACHABLE;
    } else {
        statusLabel = LDSU_STATUS.NOT_REACHABLE;
    }

    return statusLabel;
};

const LDSUOverviewReading = ({ devices, widgetId, panel }: SensorCardProps) => {
    const [dataCharts, setDataCharts] = useState<CardMetric[]>([]);
    const [ldsuInfo, setLdsuInfo] = useState<
        | (DeviceConfig & {
              statusLabel: string;
          })
        | null
    >(null);
    const [readings, setReadings] = useState<CardSensorReading[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const isMountedRef = useRef(true);

    const refreshRate = 30 * 1000; // milisesond

    const defaultAccuracy = 0;

    useEffect(() => {
        isMountedRef.current = true;

        const fetchChartData = async () => {
            const { queries, attributes } = panel;
            if (!attributes?.[0] || !devices.length) {
                return;
            }

            try {
                const gatewayId = attributes[0].gateway_id;
                const deviceId = attributes[0].device_id;

                const [chartMetric, newReadings, ldsuInfo] = await Promise.all([
                    getCardMetric({
                        queries: queries,
                    }),
                    getCardSensorReadings({ gatewayId, deviceId, devices }),
                    fetchLDSUInfo(gatewayId, deviceId),
                ]);

                if (!isMountedRef.current) return;

                if (ldsuInfo) {
                    const statusLabel = getLDSUStatusLabel(
                        newReadings ?? [],
                        ldsuInfo
                    );
                    setLdsuInfo({
                        ...ldsuInfo,
                        statusLabel,
                    });
                } else {
                    setLdsuInfo(null);
                }

                setReadings(newReadings ?? []);

                setDataCharts(chartMetric as any);
            } catch {}
        };

        (async () => {
            await fetchChartData();
        })();

        const fetchMetricInterval = setInterval(async () => {
            await fetchChartData();
        }, refreshRate);

        return () => {
            isMountedRef.current = false;
            clearInterval(fetchMetricInterval);
        };
    }, [devices, refreshRate, panel]);

    const getReadingValue = (
        said: number,
        accuracy: number | null,
        unit: string
    ) => {
        const readingData = readings.find(
            (d) => Number(d.said) === Number(said)
        );

        if (
            readingData?.value === null ||
            readingData?.value === undefined ||
            readingData?.lastActive === null ||
            readingData?.lastActive === undefined
        ) {
            return "-";
        }

        return getCurrentValueToDisplay(
            readingData.value,
            readingData.lastActive,
            Number(accuracy ?? defaultAccuracy),
            unit
        );
    };

    const getReadingDateTime = (said: number) => {
        const readingData = readings.find(
            (d) => Number(d.said) === Number(said)
        );

        return getReadingDateTimeToDisplay(readingData?.lastActive ?? null);
    };

    const renderSensor = () => {
        return (
            <Box width="100%">
                <Box className="d-flex justify-content-between mb-2">
                    <div>
                        <SensorLabel
                            fontSize={16}
                            className="text-truncate mb-1"
                        >
                            {ldsuInfo?.name}
                        </SensorLabel>
                        <SensorType fontSize={14} className="text-truncate">
                            {ldsuInfo?.UID}
                        </SensorType>
                    </div>
                    <div>
                        <div className="d-flex align-items-center ldsuList-row-status overflow-hidden">
                            {ldsuInfo?.statusLabel && (
                                <CircleIcon
                                    style={{
                                        color: getLDSUStatusColor(
                                            ldsuInfo.statusLabel
                                        ),
                                        marginRight: "5px",
                                        width: "0.9rem",
                                    }}
                                />
                            )}{" "}
                            <IOTPToolTip message={ldsuInfo?.statusLabel ?? ""}>
                                <Typography noWrap>
                                    {ldsuInfo?.statusLabel}
                                </Typography>
                            </IOTPToolTip>
                        </div>
                    </div>
                </Box>

                <div className="d-flex flex-wrap gap-2">
                    {devices.map(
                        ({
                            sensorType,
                            deviceId,
                            said,
                            sensorName,
                            accuracy,
                            chartColor,
                            unit,
                            gatewayId,
                            cls,
                            format,
                        }) => {
                            const isBinary = isBinarySensor(cls, format);
                            const chartPoints =
                                dataCharts.find(
                                    (d) =>
                                        d.said === Number(said) &&
                                        d.gatewayId === gatewayId &&
                                        d.ldsuUId === deviceId
                                )?.value ?? [];
                            const sparklineSeries = sensorName
                                ? buildSparklineSeriesWithMissingGaps({
                                      points: chartPoints,
                                      color: chartColor,
                                      isBinaryChart: isBinary,
                                      accuracy: accuracy ?? defaultAccuracy,
                                  })
                                : [];

                            return (
                                <Box
                                    key={`${widgetId}-${deviceId}-${said}`}
                                    style={{
                                        minWidth: "100px",
                                        width: "calc(50% - 0.25rem)",
                                        backgroundColor: "#3E4B67",
                                        padding: "15px",
                                        borderRadius: "5px",
                                        height: "160px",
                                        maxHeight: "160px",
                                    }}
                                >
                                    <SensorContent>
                                        <IconCircle size={35} className="mr-2">
                                            {sensorName ? (
                                                <SensorIcon
                                                    sensorType={sensorType}
                                                    cls={cls}
                                                />
                                            ) : (
                                                <ErrorOutlineOutlined
                                                    color="error"
                                                    fontSize="large"
                                                />
                                            )}
                                        </IconCircle>
                                        <SensorInfo>
                                            <SensorLabel
                                                fontSize={16}
                                                className="text-truncate"
                                            >
                                                {sensorName ?? UnknownDevice}
                                            </SensorLabel>
                                            <SensorType
                                                fontSize={14}
                                                className="text-truncate"
                                            >
                                                {sensorType}
                                            </SensorType>
                                        </SensorInfo>
                                    </SensorContent>
                                    <div
                                        className="d-flex align-items-center flex-wrap gap-2 justify-content-between w-100"
                                        style={{ marginTop: "10px" }}
                                    >
                                        {ldsuInfo &&
                                            sensorName &&
                                            sparklineSeries.length > 0 && (
                                                <SparklineChart
                                                    gapSplitSeries={
                                                        sparklineSeries
                                                    }
                                                    color={chartColor}
                                                    width={100}
                                                />
                                            )}
                                        <div
                                            className="d-flex flex-column align-items-end text-end flex-grow-1"
                                            style={{ minWidth: 0 }}
                                        >
                                            <Value>
                                                {getReadingValue(
                                                    said,
                                                    accuracy,
                                                    unit
                                                )}
                                            </Value>
                                            <ReadingTimeText
                                                fontSize={14}
                                                className="text-truncate mt-1"
                                                style={{ maxWidth: "100%" }}
                                            >
                                                {getReadingDateTime(said)}
                                            </ReadingTimeText>
                                        </div>
                                    </div>
                                </Box>
                            );
                        }
                    )}
                </div>
            </Box>
        );
    };

    return (
        <CardContent ref={containerRef} className="mt-2 mt-sm-0">
            {renderSensor()}
        </CardContent>
    );
};

export default LDSUOverviewReading;
